import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  author: string;
  excerpt?: string;
  category: string;
  tags?: string;
  featuredImage?: string;
  publishedAt: string;
  createdAt: string;
}

const BlogListingPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [postsPerPage, setPostsPerPage] = useState<number>(9);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchBlogPosts();
    fetchCategories();
  }, []);

  useEffect(() => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => {
        // Match if category matches OR if category appears in tags
        if (post.category === selectedCategory) return true;
        if (post.tags && post.tags.includes(selectedCategory)) return true;
        return false;
      });
    }

    // Filter by search term
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(post => {
        const titleMatch = post.title.toLowerCase().includes(searchLower);
        const excerptMatch = post.excerpt?.toLowerCase().includes(searchLower);
        const authorMatch = post.author.toLowerCase().includes(searchLower);
        const tagsMatch = post.tags?.toLowerCase().includes(searchLower);
        const categoryMatch = post.category.toLowerCase().includes(searchLower);
        return titleMatch || excerptMatch || authorMatch || tagsMatch || categoryMatch;
      });
    }

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter(post => {
        const postDate = new Date(post.publishedAt || post.createdAt);
        postDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
        
        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); // End of day
          return postDate >= start && postDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          return postDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); // End of day
          return postDate <= end;
        }
        return true;
      });
    }

    // Sort by date
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.createdAt).getTime();
      const dateB = new Date(b.publishedAt || b.createdAt).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredPosts(filtered);
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, sortOrder, startDate, endDate, posts]);

  useEffect(() => {
    // Reset to page 1 when posts per page changes
    setCurrentPage(1);
  }, [postsPerPage]);

  const fetchBlogPosts = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      // Fetch all blog posts, sorted by newest first
      const response = await fetch(`${API_URL}/api/blog-posts?isPublished=true&limit=1000&sortBy=publishedAt&sortOrder=desc`);
      const data = await response.json();
      const postsArray = data.posts || data || [];
      console.log(`Fetched ${postsArray.length} blog posts`);
      setPosts(postsArray);
      setFilteredPosts(postsArray);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const response = await fetch(`${API_URL}/api/blog-posts/meta/categories`);
      const data = await response.json();
      setCategories(['All', ...data]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Date not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  const goToPage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 7;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="blog-listing-page">
      {/* Header Section */}
      <section className="blog-listing-header">
        <div className="blog-listing-header-content">
          <h1>Alert1 Medical Alert News</h1>
          <p className="blog-listing-subtitle">
            The latest tips, tricks, and news related to medical alert systems, aging in place, and technology for seniors.
          </p>

          {/* Subscription Box */}
          <div className="blog-subscription-box">
            <label htmlFor="email-subscribe" className="subscription-label">Get Posts Via Email</label>
            <div className="subscription-form">
              <input 
                type="email" 
                id="email-subscribe"
                placeholder="Email: john@alert1.com" 
                className="subscription-input"
              />
              <button className="subscription-button">Subscribe</button>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Sort Controls */}
      <section className="blog-search-sort-section" style={{ background: '#f9f9f9', padding: '1.5rem 2rem', borderBottom: '1px solid #e0e0e0' }}>
        <div className="blog-posts-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* First Row: Search and Sort Controls */}
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '1rem' }}>
            {/* Search Input */}
            <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Sort and Posts Per Page Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              {/* Sort Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="sort-select" style={{ fontSize: '0.9rem', color: '#666' }}>Sort by:</label>
                <select
                  id="sort-select"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>

              {/* Posts Per Page Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="posts-per-page-select" style={{ fontSize: '0.9rem', color: '#666' }}>Show:</label>
                <select
                  id="posts-per-page-select"
                  value={postsPerPage}
                  onChange={(e) => setPostsPerPage(Number(e.target.value))}
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    outline: 'none',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="9">9 posts</option>
                  <option value="18">18 posts</option>
                  <option value="27">27 posts</option>
                  <option value="36">36 posts</option>
                  <option value="45">45 posts</option>
                  <option value="50">50 posts</option>
                </select>
              </div>
            </div>
          </div>

          {/* Second Row: Date Range Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <label htmlFor="start-date" style={{ fontSize: '0.9rem', color: '#666' }}>Date Range:</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
                placeholder="Start date"
              />
              <span style={{ color: '#666', fontSize: '0.9rem' }}>to</span>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  outline: 'none',
                  backgroundColor: 'white'
                }}
                placeholder="End date"
              />
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <nav className="blog-category-nav-section">
        <div className="blog-category-nav-wrapper">
          <a href="/" className="blog-home-icon">🏠</a>
          <div className="blog-category-pills">
            {categories.map((category) => (
              <button
                key={category}
                className={`blog-category-pill ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Blog Posts List */}
      <section className="blog-posts-section">
        <div className="blog-posts-container">
          {loading ? (
            <p>Loading blog posts...</p>
          ) : filteredPosts.length === 0 ? (
            <p>No blog posts found.</p>
          ) : (
            <>
              {/* Pagination Controls - Top */}
              {totalPages > 1 && (
                <div className="pagination-container" style={{ marginBottom: '2rem' }}>
                  <div className="pagination-info">
                    Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, filteredPosts.length)} of {filteredPosts.length} posts
                  </div>
                  
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn pagination-prev"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-top-${index}`} className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            key={page}
                            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => goToPage(page as number)}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn pagination-next"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              <div className="blog-posts-grid">
                {currentPosts.map((post) => {
                  return (
                    <article key={post.id} className="blog-post-item">
                      <Link to={`/blog/${post.slug}`} className="blog-post-link">
                        <h2 className="blog-post-item-title">{post.title}</h2>
                      </Link>
                      <div className="blog-post-meta">
                        <span className="blog-post-author">By {post.author}</span>
                        <span className="blog-post-separator">|</span>
                        <span className="blog-post-date">{formatDate(post.publishedAt || post.createdAt)}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`} className="blog-post-read-more">
                        Read More
                      </Link>
                    </article>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, filteredPosts.length)} of {filteredPosts.length} posts
                  </div>
                  
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn pagination-prev"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-bottom-${index}`} className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            key={page}
                            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => goToPage(page as number)}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn pagination-next"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogListingPage;

